// src/monitoring/pages/Alarms.jsx - the alarm list, the rules and who is told.

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link as RouterLink, useSearchParams } from "react-router-dom";
import {
  Alert, Box, Button, Checkbox, Chip, CircularProgress, FormControlLabel, IconButton, MenuItem, Paper, Stack, Table, TableBody, TableCell, TableHead, TableRow, TextField, Typography,
} from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { useMonitoring } from "../MonitoringContext";
import { alarmMessage, ruleDescription, useMonitoringText } from "../i18n";
import { useLanguage } from "../../context/LanguageContext";
import { ackAlarm, fetchAlarmRules, fetchAlarms, fetchNotificationRules, resolveAlarm, saveAlarmRules, saveNotificationRules } from "../services/monitoringApi";
import { SectionTitle, SeverityChip, fmtDateTime, useLocale } from "../components/primitives";

const SEVERITIES = ["info", "warning", "critical"];
const PARAM_PCT = new Set(["ratio", "ratio_weather", "ratio_clearsky", "deviation", "siblings_min_share", "zero_share", "min_expected_kwh_share"]);

function AlarmTable({ alarms, tzOf, onAck, onResolve, busy }) {
  const t = useMonitoringText();
  const { language } = useLanguage();
  const locale = useLocale();
  if (!alarms.length) return <Typography color="text.secondary">{t("alarms.empty")}</Typography>;
  return (
    <Box sx={{ overflowX: "auto" }}>
      <Table size="small">
        <TableHead>
          <TableRow>
            {["severity", "park", "device", "rule", "message", "opened", "closed", "actions"].map((k) => <TableCell key={k}>{t(`alarms.col.${k}`)}</TableCell>)}
          </TableRow>
        </TableHead>
        <TableBody>
          {alarms.map((a) => (
            <TableRow key={a.id} hover sx={{ opacity: a.closed_at ? 0.65 : 1 }}>
              <TableCell><SeverityChip severity={a.severity} /></TableCell>
              <TableCell><Typography variant="body2" component={RouterLink} to={`/monitoring/parks/${a.source_id}`} sx={{ color: "primary.main", textDecoration: "none", fontWeight: 600 }}>{a.park}</Typography></TableCell>
              <TableCell>{a.device || "—"}</TableCell>
              <TableCell sx={{ whiteSpace: "nowrap" }}>{t(`rule.${a.rule}`)}</TableCell>
              <TableCell>{alarmMessage(language, a)}</TableCell>
              <TableCell sx={{ whiteSpace: "nowrap" }}>{fmtDateTime(a.opened_at, locale, tzOf(a.source_id))}</TableCell>
              <TableCell sx={{ whiteSpace: "nowrap" }}>{a.closed_at ? fmtDateTime(a.closed_at, locale, tzOf(a.source_id)) : "—"}</TableCell>
              <TableCell sx={{ whiteSpace: "nowrap" }}>
                {!a.closed_at && (
                  <Stack direction="row" spacing={0.5}>
                    {a.acknowledged_at ? <Chip size="small" variant="outlined" label={t("alarms.acked")} /> : <Button size="small" disabled={busy} onClick={() => onAck(a)}>{t("alarms.ack")}</Button>}
                    <Button size="small" color="secondary" disabled={busy} onClick={() => onResolve(a)}>{t("alarms.resolve")}</Button>
                  </Stack>
                )}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Box>
  );
}

function RulesEditor({ projectId, accessToken }) {
  const t = useMonitoringText();
  const { language } = useLanguage();
  const [rules, setRules] = useState(null);
  const [saving, setSaving] = useState(false);
  const [note, setNote] = useState(null);
  useEffect(() => {
    fetchAlarmRules(projectId, { accessToken }).then((r) => setRules(r.rules)).catch((e) => setNote({ severity: "error", text: e.message }));
  }, [projectId, accessToken]);
  const update = (i, patch) => setRules((rs) => rs.map((r, j) => (j === i ? { ...r, ...patch } : r)));
  const updateParam = (i, key, value) => setRules((rs) => rs.map((r, j) => (j === i ? { ...r, params: { ...r.params, [key]: value } } : r)));
  const save = async () => {
    setSaving(true); setNote(null);
    try {
      const body = rules.map((r) => ({ rule: r.rule, enabled: r.enabled, severity: r.severity,
        params: Object.fromEntries(Object.entries(r.params).map(([k, v]) => [k, Number.isFinite(Number(v)) ? Number(v) : v])) }));
      const res = await saveAlarmRules(projectId, body, { accessToken });
      setRules(res.rules); setNote({ severity: "success", text: t("alarms.rules.saved") });
    } catch (e) {
      setNote({ severity: "error", text: e.message });
    } finally {
      setSaving(false);
    }
  };
  if (!rules) return <CircularProgress size={20} />;
  return (
    <Stack spacing={1.5}>
      {rules.map((r, i) => (
        <Paper key={r.rule} variant="outlined" sx={{ p: 1.5, borderRadius: 1.5 }}>
          <Stack direction={{ xs: "column", md: "row" }} spacing={1.5} alignItems={{ md: "center" }}>
            <FormControlLabel sx={{ minWidth: 280, m: 0 }} control={<Checkbox checked={Boolean(r.enabled)} onChange={(e) => update(i, { enabled: e.target.checked })} />}
              label={<Typography variant="body2" sx={{ fontWeight: 600 }}>{t(`rule.${r.rule}`)}</Typography>} />
            <TextField select size="small" label={t("alarms.rule.severity")} value={r.severity} onChange={(e) => update(i, { severity: e.target.value })} sx={{ minWidth: 150 }}>
              {SEVERITIES.map((s) => <MenuItem key={s} value={s}>{t(`severity.${s}`)}</MenuItem>)}
            </TextField>
            <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ flex: 1 }}>
              {Object.entries(r.params).map(([k, v]) => (
                <TextField key={k} size="small" label={k} value={PARAM_PCT.has(k) ? Math.round(Number(v) * 1000) / 10 : v}
                  onChange={(e) => updateParam(i, k, PARAM_PCT.has(k) ? Number(e.target.value) / 100 : e.target.value)}
                  slotProps={{ input: { endAdornment: PARAM_PCT.has(k) ? <Typography variant="caption" sx={{ ml: 0.5 }}>%</Typography> : null } }} sx={{ width: 150 }} />
              ))}
            </Stack>
          </Stack>
          <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.75 }}>{ruleDescription(language, r.rule, r.params)}</Typography>
        </Paper>
      ))}
      {note && <Alert severity={note.severity}>{note.text}</Alert>}
      <Box><Button variant="contained" onClick={save} disabled={saving}>{t("alarms.rules.save")}</Button></Box>
    </Stack>
  );
}

function NotificationsEditor({ projectId, accessToken }) {
  const t = useMonitoringText();
  const [rules, setRules] = useState(null);
  const [mailConfigured, setMailConfigured] = useState(false);
  const [saving, setSaving] = useState(false);
  const [note, setNote] = useState(null);
  useEffect(() => {
    fetchNotificationRules(projectId, { accessToken }).then((r) => { setRules(r.rules); setMailConfigured(Boolean(r.email_delivery_configured)); })
      .catch((e) => setNote({ severity: "error", text: e.message }));
  }, [projectId, accessToken]);
  const save = async () => {
    setSaving(true); setNote(null);
    try {
      const res = await saveNotificationRules(projectId, rules.filter((r) => r.target.trim()), { accessToken });
      setRules(res.rules); setNote({ severity: "success", text: t("alarms.notif.saved") });
    } catch (e) {
      setNote({ severity: "error", text: e.message });
    } finally {
      setSaving(false);
    }
  };
  if (!rules) return <CircularProgress size={20} />;
  return (
    <Stack spacing={1.5}>
      <Alert severity={mailConfigured ? "success" : "info"}>{mailConfigured ? t("alarms.notif.mailConfigured") : t("alarms.notif.mailNotConfigured")}</Alert>
      {rules.map((r, i) => (
        <Stack key={r.id || i} direction={{ xs: "column", sm: "row" }} spacing={1} alignItems={{ sm: "center" }}>
          <TextField select size="small" label={t("alarms.notif.channel")} value={r.channel} sx={{ minWidth: 120 }}
            onChange={(e) => setRules((rs) => rs.map((x, j) => (j === i ? { ...x, channel: e.target.value } : x)))}>
            {["email", "sms", "viber"].map((c) => <MenuItem key={c} value={c}>{c === "email" ? "E-mail" : c === "sms" ? "SMS" : "Viber"}</MenuItem>)}
          </TextField>
          <TextField size="small" label={t("alarms.notif.target")} value={r.target} sx={{ flex: 1, minWidth: 220 }}
            onChange={(e) => setRules((rs) => rs.map((x, j) => (j === i ? { ...x, target: e.target.value } : x)))} />
          <TextField select size="small" label={t("alarms.notif.minSeverity")} value={r.min_severity || "warning"} sx={{ minWidth: 160 }}
            onChange={(e) => setRules((rs) => rs.map((x, j) => (j === i ? { ...x, min_severity: e.target.value } : x)))}>
            {SEVERITIES.map((s) => <MenuItem key={s} value={s}>{t(`severity.${s}`)}</MenuItem>)}
          </TextField>
          <IconButton aria-label={t("sources.delete")} onClick={() => setRules((rs) => rs.filter((_, j) => j !== i))}><DeleteOutlineIcon /></IconButton>
        </Stack>
      ))}
      {note && <Alert severity={note.severity}>{note.text}</Alert>}
      <Stack direction="row" spacing={1}>
        <Button onClick={() => setRules((rs) => [...rs, { channel: "email", target: "", min_severity: "warning", enabled: true }])}>{t("alarms.notif.add")}</Button>
        <Button variant="contained" onClick={save} disabled={saving}>{t("alarms.notif.save")}</Button>
      </Stack>
    </Stack>
  );
}

export default function Alarms() {
  const m = useMonitoring();
  const t = useMonitoringText();
  const [params, setParams] = useSearchParams();
  const status = params.get("status") || "open";
  const park = params.get("park") || "";
  const [alarms, setAlarms] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const tzOf = useCallback((sid) => m.parks.find((p) => p.id === sid)?.plant?.timezone || "Europe/Athens", [m.parks]);

  const load = useCallback(() => {
    if (!m.projectId || !m.accessToken) return;
    setError(null);
    fetchAlarms(m.projectId, { status, source_id: park || undefined }, { accessToken: m.accessToken })
      .then((r) => setAlarms(r.alarms)).catch((e) => setError(e.message));
  }, [m.projectId, m.accessToken, status, park]);
  useEffect(load, [load, m.portfolio]);

  const act = async (fn, alarm) => {
    setBusy(true);
    try { await fn(alarm.id, { accessToken: m.accessToken }); load(); m.refresh(); } catch (e) { setError(e.message); } finally { setBusy(false); }
  };
  const setParam = (k, v) => { const next = new URLSearchParams(params); if (v) next.set(k, v); else next.delete(k); setParams(next, { replace: true }); };
  const counts = useMemo(() => (alarms || []).reduce((acc, a) => { if (!a.closed_at) acc[a.severity] = (acc[a.severity] || 0) + 1; return acc; }, {}), [alarms]);

  return (
    <Stack spacing={2.5}>
      <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
        <SectionTitle title={t("alarms.title")} hint={t("alarms.subtitle")} />
        <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} sx={{ mb: 2 }} alignItems={{ sm: "center" }}>
          <TextField select size="small" label={t("alarms.filter.status")} value={status} onChange={(e) => setParam("status", e.target.value)} sx={{ minWidth: 140 }}>
            {["open", "closed", "all"].map((s) => <MenuItem key={s} value={s}>{t(`alarms.filter.${s}`)}</MenuItem>)}
          </TextField>
          <TextField select size="small" label={t("alarms.filter.park")} value={park} onChange={(e) => setParam("park", e.target.value)} sx={{ minWidth: 220 }}>
            <MenuItem value="">{t("alarms.filter.allParks")}</MenuItem>
            {m.parks.map((p) => <MenuItem key={p.id} value={p.id}>{p.name}</MenuItem>)}
          </TextField>
          <Box sx={{ flex: 1 }} />
          {counts.critical > 0 && <Chip color="error" size="small" label={`${counts.critical} ${t("severity.critical").toLowerCase()}`} />}
          {counts.warning > 0 && <Chip color="warning" size="small" label={`${counts.warning} ${t("severity.warning").toLowerCase()}`} />}
          {counts.info > 0 && <Chip color="info" size="small" label={`${counts.info} ${t("severity.info").toLowerCase()}`} />}
        </Stack>
        {error && <Alert severity="error" sx={{ mb: 1.5 }}>{error}</Alert>}
        {alarms === null ? <CircularProgress size={22} /> : <AlarmTable alarms={alarms} tzOf={tzOf} busy={busy} onAck={(a) => act(ackAlarm, a)} onResolve={(a) => act(resolveAlarm, a)} />}
      </Paper>
      <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
        <SectionTitle title={t("alarms.rules")} hint={t("alarms.rulesHint")} />
        <RulesEditor projectId={m.projectId} accessToken={m.accessToken} />
      </Paper>
      <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
        <SectionTitle title={t("alarms.notifications")} hint={t("alarms.notificationsHint")} />
        <NotificationsEditor projectId={m.projectId} accessToken={m.accessToken} />
      </Paper>
    </Stack>
  );
}
